import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setPendingDownloadAfterContact, setPopupState } from '../../model/configuratorSlice';
import { CloseButton } from '../../shared/svg/components';
import { PopupState } from '../../model/constants';
import ContactUsService from '../../services/ContactUsService';
import { hasContactFormBeenSent } from '../../utils/contactFormStorage';
import DownloadPdfService from '../../services/DownloadPdfService';

const COUNTRY_CODES = [
  { code: '+49', flag: '🇩🇪', name: 'Alemania' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: '+32', flag: '🇧🇪', name: 'Bélgica' },
  { code: '+55', flag: '🇧🇷', name: 'Brasil' },
  { code: '+359', flag: '🇧🇬', name: 'Bulgaria' },
  { code: '+1', flag: '🇨🇦', name: 'Canadá' },
  { code: '+420', flag: '🇨🇿', name: 'Chequia' },
  { code: '+56', flag: '🇨🇱', name: 'Chile' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+57', flag: '🇨🇴', name: 'Colombia' },
  { code: '+82', flag: '🇰🇷', name: 'Corea del Sur' },
  { code: '+506', flag: '🇨🇷', name: 'Costa Rica' },
  { code: '+385', flag: '🇭🇷', name: 'Croacia' },
  { code: '+53', flag: '🇨🇺', name: 'Cuba' },
  { code: '+45', flag: '🇩🇰', name: 'Dinamarca' },
  { code: '+593', flag: '🇪🇨', name: 'Ecuador' },
  { code: '+20', flag: '🇪🇬', name: 'Egipto' },
  { code: '+971', flag: '🇦🇪', name: 'Emiratos Árabes Unidos' },
  { code: '+421', flag: '🇸🇰', name: 'Eslovaquia' },
  { code: '+386', flag: '🇸🇮', name: 'Eslovenia' },
  { code: '+34', flag: '🇪🇸', name: 'España' },
  { code: '+1', flag: '🇺🇸', name: 'Estados Unidos' },
  { code: '+372', flag: '🇪🇪', name: 'Estonia' },
  { code: '+358', flag: '🇫🇮', name: 'Finlandia' },
  { code: '+33', flag: '🇫🇷', name: 'Francia' },
  { code: '+30', flag: '🇬🇷', name: 'Grecia' },
  { code: '+502', flag: '🇬🇹', name: 'Guatemala' },
  { code: '+852', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+36', flag: '🇭🇺', name: 'Hungría' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+353', flag: '🇮🇪', name: 'Irlanda' },
  { code: '+354', flag: '🇮🇸', name: 'Islandia' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+39', flag: '🇮🇹', name: 'Italia' },
  { code: '+81', flag: '🇯🇵', name: 'Japón' },
  { code: '+371', flag: '🇱🇻', name: 'Letonia' },
  { code: '+370', flag: '🇱🇹', name: 'Lituania' },
  { code: '+352', flag: '🇱🇺', name: 'Luxemburgo' },
  { code: '+212', flag: '🇲🇦', name: 'Marruecos' },
  { code: '+52', flag: '🇲🇽', name: 'México' },
  { code: '+373', flag: '🇲🇩', name: 'Moldavia' },
  { code: '+377', flag: '🇲🇨', name: 'Mónaco' },
  { code: '+382', flag: '🇲🇪', name: 'Montenegro' },
  { code: '+47', flag: '🇳🇴', name: 'Noruega' },
  { code: '+64', flag: '🇳🇿', name: 'Nueva Zelanda' },
  { code: '+31', flag: '🇳🇱', name: 'Países Bajos' },
  { code: '+507', flag: '🇵🇦', name: 'Panamá' },
  { code: '+51', flag: '🇵🇪', name: 'Perú' },
  { code: '+48', flag: '🇵🇱', name: 'Polonia' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+44', flag: '🇬🇧', name: 'Reino Unido' },
  { code: '+1', flag: '🇩🇴', name: 'República Dominicana' },
  { code: '+40', flag: '🇷🇴', name: 'Rumanía' },
  { code: '+7', flag: '🇷🇺', name: 'Rusia' },
  { code: '+381', flag: '🇷🇸', name: 'Serbia' },
  { code: '+65', flag: '🇸🇬', name: 'Singapur' },
  { code: '+27', flag: '🇿🇦', name: 'Sudáfrica' },
  { code: '+46', flag: '🇸🇪', name: 'Suecia' },
  { code: '+41', flag: '🇨🇭', name: 'Suiza' },
  { code: '+90', flag: '🇹🇷', name: 'Turquía' },
  { code: '+380', flag: '🇺🇦', name: 'Ucrania' },
  { code: '+598', flag: '🇺🇾', name: 'Uruguay' },
  { code: '+58', flag: '🇻🇪', name: 'Venezuela' },
];

const CountryCodeSelect = ({ value, countryName, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selected = COUNTRY_CODES.find(c => c.code === value && c.name === countryName)
    || COUNTRY_CODES.find(c => c.code === value);

  const filtered = COUNTRY_CODES.filter(c => c.name.toLowerCase().includes(search.toLowerCase())
    || c.code.includes(search));

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="country-code-select" ref={ref}>
      <button type="button" className="country-code-trigger" onClick={() => setOpen(o => !o)}>
        {selected ? `${selected.flag} ${selected.code}` : value}
        <span className="country-code-arrow">▾</span>
      </button>
      {open && (
        <div className="country-code-dropdown">
          <input
            className="country-code-search"
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <ul>
            {filtered.map(({ code, flag, name }) => (
              <li key={`${name}-${code}`}>
                <button
                  type="button"
                  onClick={() => { onChange(code, name); setOpen(false); setSearch(''); }}
                >
                  {flag} {name} <span className="country-code-num">{code}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

CountryCodeSelect.propTypes = {
  value: PropTypes.string.isRequired,
  countryName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const isValidEmail = val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

const isValidPhone = val => /^[\d\s\-().+]{6,15}$/.test(val.trim());

const ContactUsPopup = () => {
    const { promotionSlug } = useParams();
    const { popupState, pendingDownloadAfterContact } = useSelector(state => state.configurator);
    const dispatch = useDispatch();

    const [nombre, setNombre] = useState('');
    const [apellidos, setApellidos] = useState('');
    const [telefono, setTelefono] = useState('');
    const [email, setEmail] = useState('');
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [countryCode, setCountryCode] = useState('+34');
    const [countryName, setCountryName] = useState('España');
    const [touched, setTouched] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const contactUsMutation = ContactUsService();
    const downloadPdfMutation = DownloadPdfService();

    const isOpened = popupState === PopupState.ContactUs;

    const closePopupHandler = () => {
        dispatch(setPopupState(PopupState.None));
        setNombre('');
        setApellidos('');
        setTelefono('');
        setEmail('');
        setPrivacyAccepted(false);
        setSubmitted(false);
        setCountryCode('+34');
        setCountryName('España');
        setTouched(false);
        setSubmitError(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setTouched(true);
        if (!privacyAccepted || !nombre || !apellidos || !isValidPhone(telefono) || !isValidEmail(email)) return;
        if (contactUsMutation.isLoading) return;
        if (hasContactFormBeenSent(promotionSlug)) {
            setSubmitted(true);
            return;
        }

        contactUsMutation.mutate(
            {
                nombre,
                apellidos,
                telefono: `${countryCode}${telefono}`,
                email,
            },
            {
                onSuccess: () => {
                    setSubmitError(false);
                    if (pendingDownloadAfterContact) {
                        dispatch(setPendingDownloadAfterContact(false));
                        dispatch(setPopupState(PopupState.None));
                        downloadPdfMutation.mutate();
                    } else {
                        setSubmitted(true);
                    }
                },
                onError: () => {
                    setSubmitError(true);
                },
            },
        );
    };

    if (!isOpened) return null;

    return (
      <div className="contact-us-popup opened">
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <button className="background opened" type="button" onClick={closePopupHandler} />
        <div className="panel opened">
          <div className="header">Datos de Contacto</div>

          {submitted ? (
            <div className="submitted-message">
              <p>Gracias por contactar con nosotros. Nos pondremos en contacto contigo en breve.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-field form-field--nombre">
                  <label className="field-label" htmlFor="contact-us-nombre">Nombre *</label>
                  <input
                    id="contact-us-nombre"
                    type="text"
                    value={nombre}
                    maxLength={40}
                    onChange={e => setNombre(e.target.value)}
                    className={touched && !nombre ? 'input-error' : ''}
                  />
                  {touched && !nombre && <span className="field-error">Este campo es obligatorio</span>}
                </div>
                <div className="form-field form-field--apellidos">
                  <label className="field-label" htmlFor="contact-us-apellidos">Apellidos *</label>
                  <input
                    id="contact-us-apellidos"
                    type="text"
                    value={apellidos}
                    maxLength={35}
                    onChange={e => setApellidos(e.target.value)}
                    className={touched && !apellidos ? 'input-error' : ''}
                  />
                  {touched && !apellidos && <span className="field-error">Este campo es obligatorio</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-field form-field--telefono">
                  <label className="field-label" htmlFor="contact-us-telefono">Teléfono de contacto *</label>
                  <div className={`phone-input-row${
                          touched && (!telefono || !isValidPhone(telefono)) ? ' input-error-row' : ''
                        }`}
                  >
                    <CountryCodeSelect
                      value={countryCode}
                      countryName={countryName}
                      onChange={(code, name) => { setCountryCode(code); setCountryName(name); }}
                    />
                    <input
                      id="contact-us-telefono"
                      type="tel"
                      value={telefono}
                      onChange={e => setTelefono(e.target.value)}
                      className={touched && (!telefono || !isValidPhone(telefono)) ? 'input-error' : ''}
                    />
                  </div>
                  {touched && (!telefono || !isValidPhone(telefono)) && (
                  <span className="field-error">
                    {!telefono ? 'Este campo es obligatorio' : 'Número de teléfono no válido'}
                  </span>
                        )}
                </div>
              </div>
              <div className="form-field form-field--email">
                <label className="field-label" htmlFor="contact-us-email">Email *</label>
                <input
                  id="contact-us-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={touched && (!email || !isValidEmail(email)) ? 'input-error' : ''}
                />
                {touched && (!email || !isValidEmail(email)) && (
                <span className="field-error">
                  {!email ? 'Este campo es obligatorio' : 'Dirección de email no válida'}
                </span>
                      )}
              </div>
              <div className={`form-field privacy-field${touched && !privacyAccepted ? ' privacy-error' : ''}`}>
                <label htmlFor="contact-us-privacy">
                  <input
                    id="contact-us-privacy"
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={e => setPrivacyAccepted(e.target.checked)}
                  />
                  <span>
                    Acepto la{' '}
                    <a
                      href="https://metrovacesa.com/politica-privacidad"
                      target="_blank"
                      rel="noreferrer"
                    >
                      política de privacidad
                    </a>{' '}
                    *
                  </span>
                </label>
              </div>
              <div className="container-buttons">
                <button
                  className="request-budget-button"
                  type="submit"
                  disabled={contactUsMutation.isLoading}
                >
                  <span>{contactUsMutation.isLoading ? 'Enviando...' : 'Enviar mensaje'}</span>
                </button>
                {submitError && (
                <span className="field-error submit-error">
                  Ha ocurrido un error. Por favor, prueba de nuevo más tarde.
                </span>
                      )}
              </div>
            </form>
                )}

          <button className="close-button" type="button" onClick={closePopupHandler}>
            <CloseButton />
          </button>
        </div>
      </div>
    );
};

export default ContactUsPopup;
